import { useCallback, useMemo } from 'react';
import { type Editor, useCurrentEditor, useEditorState } from '@tiptap/react';

export interface UseColorHighlightConfig {
  editor?: Editor | null;
  highlightColor?: string;
  onApplied?: ({ color }: { color: string }) => void;
}

/**
 * tiptap 라이브러리 UI와 결합도를 낮춘 hook
 */
export function useColorHighlight(config: UseColorHighlightConfig) {
  const { editor: providedEditor, highlightColor, onApplied } = config;

  const { editor: coreEditor } = useCurrentEditor();

  const mainEditor = useMemo(() => {
    return providedEditor || coreEditor;
  }, [providedEditor, coreEditor]);

  const editorState = useEditorState({
    editor: mainEditor,
    selector: ctx => {
      return {
        canHighlight:
          ctx.editor?.can().chain().toggleHighlight().run() ?? false,
        isActive: highlightColor
          ? ctx.editor?.isActive('highlight', { color: highlightColor }) ??
            false
          : ctx.editor?.isActive('highlight') ?? false,
      };
    },
  });

  const handleColorHighlight = useCallback(() => {
    if (!mainEditor || !highlightColor || !editorState?.canHighlight)
      return false;

    try {
      const success = mainEditor
        .chain()
        .focus()
        .toggleMark('highlight', { color: highlightColor })
        .run();
      if (success) {
        onApplied?.({ color: highlightColor });
      }
      return success;
    } catch (error) {
      console.error('Failed to toggle highlight:', error);
      return false;
    }
  }, [highlightColor, mainEditor, onApplied, editorState?.canHighlight]);

  const handleRemoveHighlight = useCallback(() => {
    if (!mainEditor?.isEditable || !editorState?.canHighlight) return false;

    try {
      return mainEditor.chain().focus().unsetMark('highlight').run();
    } catch (error) {
      console.error('Failed to remove highlight:', error);
      return false;
    }
  }, [mainEditor, editorState?.canHighlight]);

  return {
    isActive: editorState?.isActive ?? false,
    handleColorHighlight,
    handleRemoveHighlight,
  };
}
